import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plug, CheckCircle2, AlertTriangle, Loader2, Link2, Unplug } from "lucide-react";

export default function Parametres() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/bexio/oauth/status");
      setStatus(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const r = searchParams.get("bexio");
    if (r === "connected") toast.success("Bexio connecté avec succès");
    if (r === "error") toast.error("La connexion Bexio a échoué. Réessayez.");
    if (r) {
      searchParams.delete("bexio");
      setSearchParams(searchParams, { replace: true });
    }
    loadStatus();
  }, []); // eslint-disable-line

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/bexio/oauth/url");
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    try {
      await api.post("/bexio/oauth/disconnect");
      toast.success("Bexio déconnecté");
      loadStatus();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const isOauth = status?.mode === "oauth";
  const isLegacy = status?.mode === "token";

  return (
    <div className="p-6 md:p-12 max-w-3xl" data-testid="parametres-page">
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5E2129] mb-2">Configuration</p>
      <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mb-8">Paramètres</h1>

      <div className="bg-white border border-[#EBE5D9] rounded-2xl p-7" data-testid="bexio-connection-card">
        <div className="flex items-start gap-4 mb-5">
          <div className="w-12 h-12 rounded-xl bg-[#5E2129]/8 flex items-center justify-center shrink-0">
            <Plug className="w-6 h-6 text-[#5E2129]" />
          </div>
          <div>
            <h2 className="font-serif text-2xl text-[#1A1A1A]">Connexion Bexio</h2>
            <p className="text-sm text-[#666] mt-1">
              Connexion sécurisée OAuth : le jeton se renouvelle automatiquement, plus besoin de le remplacer manuellement.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#666]"><Loader2 className="w-5 h-5 animate-spin" /> Chargement…</div>
        ) : isOauth ? (
          <div data-testid="bexio-status-connected">
            <div className="flex items-center gap-2 rounded-xl bg-[#2E4F36]/8 text-[#2E4F36] px-4 py-3 mb-4">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold text-sm">Connecté via OAuth — renouvellement automatique actif</span>
            </div>
            {status?.email && <p className="text-sm text-[#666]">Compte : <span className="font-medium text-[#1A1A1A]">{status.email}</span></p>}
            <div className="mt-5 flex gap-3">
              <Button data-testid="bexio-reconnect-btn" onClick={connect} disabled={connecting} variant="outline" className="rounded-full border-[#5E2129]/30 text-[#5E2129] hover:bg-[#5E2129]/5">
                {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />} Reconnecter
              </Button>
              <Button data-testid="bexio-disconnect-btn" onClick={disconnect} variant="ghost" className="rounded-full text-[#9A3B3B] hover:bg-[#9A3B3B]/5">
                <Unplug className="w-4 h-4 mr-2" /> Déconnecter
              </Button>
            </div>
          </div>
        ) : (
          <div data-testid="bexio-status-disconnected">
            <div className="flex items-start gap-2 rounded-xl bg-[#D97736]/10 text-[#B45A1E] px-4 py-3 mb-4">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <span className="text-sm">
                {isLegacy
                  ? "Vous utilisez l'ancien jeton manuel qui expire tous les 90 jours. Connectez Bexio en OAuth pour ne plus jamais avoir à le remplacer."
                  : "Bexio n'est pas connecté. Connectez votre compte pour créer et envoyer des factures."}
              </span>
            </div>
            {status?.has_credentials === false && (
              <p className="text-xs text-[#9A3B3B] mb-4">
                ⚠️ Les identifiants OAuth (client_id / client_secret) ne sont pas encore configurés côté serveur.
              </p>
            )}
            <Button data-testid="bexio-connect-btn" onClick={connect} disabled={connecting || status?.has_credentials === false} className="rounded-full bg-[#5E2129] hover:bg-[#4A1A20] text-white px-6 py-3 h-auto">
              {connecting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Link2 className="w-5 h-5 mr-2" />} Connecter Bexio
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
